import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { eventBus } from '../eventBus';
import { validate } from '../contracts/validate';
import '../contracts/Orcamentos_CriarOrcamentoPDFInput.ts';
import '../contracts/Orcamentos_BuscaDadosResponse';
import '../contracts/Orcamentos_BuscaOrcamentosDoClienteResponse';
import '../contracts/Orcamentos_BuscarOrcamentoResponse';
import '../contracts/Orcamentos_CriarOrcamentoPDFResponse';
import '../contracts/Orcamentos_SalvarOrcamentoResponse';
import '../contracts/OrcamentosServicos_SelectserviciosResponse';

import '../contracts/ConsultaCep_ConsultaResponse';
import '../contracts/Orcamentos_AlterarStatusResponse';
import '../contracts/Orcamentos_AprovarOrcamentoResponse';
import '../contracts/Orcamentos_CancelarOrcamentoResponse';
import '../contracts/Orcamentos_ExcluirOrcamentoResponse';
import '../contracts/Orcamentos_UpdateOrcamentoResponse';
import '../contracts/Origens_BuscaOrigensResponse';
import '../contracts/Orcamentos_SalvarOrcamentoInput';
import '../contracts/Orcamentos_UpdateOrcamentoInput';
import '../contracts/Orcamentos_AprovarOrcamentoInput';
import '../contracts/Orcamentos_AlterarStatusInput';
import '../contracts/Orcamentos_BuscarOrcamentosParaCompromissoInput';
import '../contracts/Orcamentos_BuscarOrcamentosParaCompromissoResponse';
import type { Orcamentos_BuscarOrcamentosParaCompromissoResponse } from '../contracts/Orcamentos_BuscarOrcamentosParaCompromissoResponse';
import type { Orcamentos_BuscarOrcamentosParaCompromissoRow } from '../contracts/Orcamentos_BuscarOrcamentosParaCompromissoRow';
import type { Orcamentos_CriarOrcamentoPDFInput } from '../contracts/Orcamentos_CriarOrcamentoPDFInput';
import type { Orcamentos_SalvarOrcamentoInput } from '../contracts/Orcamentos_SalvarOrcamentoInput';
import type { Orcamentos_UpdateOrcamentoInput } from '../contracts/Orcamentos_UpdateOrcamentoInput';
/**
 * Composable for Orcamento (Budget) operations
 * Provides methods to interact with the Orcamentos API
 */
export function useOrcamento() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch list of orcamentos with filters
   */
  const fetchOrcamentos = async () => {
    log('useOrcamento', 'fetchOrcamentos', 'Fetching orcamentos list');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/orcamentos/buscaDados', {
        responseContract: 'Orcamentos_BuscaDadosResponse',
      });
      log('useOrcamento', 'fetchOrcamentos', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar orçamentos');
      }
    } catch (err) {
      log('useOrcamento', 'fetchOrcamentos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single orcamento by ID
   */
  const fetchOrcamento = async (orcamentoId: number) => {
    log('useOrcamento', 'fetchOrcamento', 'Fetching orcamento', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/orcamentos/buscar_orcamento/${orcamentoId}`, {
        responseContract: 'Orcamentos_BuscarOrcamentoResponse',
      });
      log('useOrcamento', 'fetchOrcamento', 'Response', response);

      // Return full response object so callers can access response.success and response.dados
      if (response.success) {
        eventBus.emit('orcamento.form.ready', response.dados);
        return response;
      } else {
        throw new Error(response.error || 'Erro ao buscar orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'fetchOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new orcamento
   */
  const createOrcamento = async (data: Orcamentos_SalvarOrcamentoInput) => {
    log('useOrcamento', 'createOrcamento', 'Creating orcamento', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/orcamentos/salvar_orcamento/', data, {
        responseContract: 'Orcamentos_SalvarOrcamentoResponse',
        inputContract: 'Orcamentos_SalvarOrcamentoInput'
      });
      log('useOrcamento', 'createOrcamento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'createOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update existing orcamento
   */
  const updateOrcamento = async (data: Orcamentos_UpdateOrcamentoInput) => {
    log('useOrcamento', 'updateOrcamento', 'Updating orcamento', data);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/orcamentos/update_orcamento/', data, {
        responseContract: 'Orcamentos_UpdateOrcamentoResponse',
        inputContract: 'Orcamentos_UpdateOrcamentoInput'
      });
      log('useOrcamento', 'updateOrcamento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao atualizar orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'updateOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Cancel orcamento
   */
  const cancelOrcamento = async (orcamentoId: number) => {
    log('useOrcamento', 'cancelOrcamento', 'Cancelling orcamento', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/orcamentos/cancelarOrcamento/${orcamentoId}`, { responseContract: 'Orcamentos_CancelarOrcamentoResponse' });
      log('useOrcamento', 'cancelOrcamento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao cancelar orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'cancelOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete orcamento (Full permission only)
   */
  const deleteOrcamento = async (orcamentoId: number) => {
    log('useOrcamento', 'deleteOrcamento', 'Deleting orcamento', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/orcamentos/excluirOrcamento/${orcamentoId}`, { responseContract: 'Orcamentos_ExcluirOrcamentoResponse' });
      log('useOrcamento', 'deleteOrcamento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao excluir orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'deleteOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * "Não encontrou o orçamento?" da agenda (chamado DS 2026-0068): busca por cliente ou número em
   * todos os orçamentos das franquias do filtro, cada um com os motivos de não estar na lista.
   */
  const buscarOrcamentosForaDaAgenda = async (search: string): Promise<Orcamentos_BuscarOrcamentosParaCompromissoRow[]> => {
    log('useOrcamento', 'buscarOrcamentosForaDaAgenda', 'Searching', search);
    const response = await httpService.post<Orcamentos_BuscarOrcamentosParaCompromissoResponse>(
      '/index.php/orcamentos/buscar_orcamentos_para_compromisso',
      { search },
      { inputContract: 'Orcamentos_BuscarOrcamentosParaCompromissoInput', responseContract: 'Orcamentos_BuscarOrcamentosParaCompromissoResponse' }
    );
    if (!response.success) {
      throw new Error(response.error || 'Erro ao buscar orçamentos');
    }
    return (response.dados as Orcamentos_BuscarOrcamentosParaCompromissoRow[] | undefined) ?? [];
  };

  /**
   * Approve orcamento (change status and prepare for scheduling)
   */
  const aprovarOrcamento = async (orcamentoId: number) => {
    log('useOrcamento', 'aprovarOrcamento', 'Approving orcamento', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/orcamentos/aprovar_orcamento/${orcamentoId}/`, null, { inputContract: 'Orcamentos_AprovarOrcamentoInput', responseContract: 'Orcamentos_AprovarOrcamentoResponse' });
      log('useOrcamento', 'aprovarOrcamento', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao aprovar orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'aprovarOrcamento', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Change orcamento status
   */
  const changeStatus = async (orcamentoId: number, status: string) => {
    log('useOrcamento', 'changeStatus', 'Changing status', { orcamentoId, status });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/orcamentos/alterar_status/${orcamentoId}/${status}`, null, { inputContract: 'Orcamentos_AlterarStatusInput', responseContract: 'Orcamentos_AlterarStatusResponse' });
      log('useOrcamento', 'changeStatus', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao alterar status do orçamento');
      }
    } catch (err) {
      log('useOrcamento', 'changeStatus', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch available services with categories
   */
  const fetchServicos = async () => {
    log('useOrcamento', 'fetchServicos', 'Fetching services');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/orcamentos_servicos/selectServicos', {
        responseContract: 'OrcamentosServicos_SelectserviciosResponse',
      });
      log('useOrcamento', 'fetchServicos', 'Response', response);

      if (response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao buscar serviços');
      }
    } catch (err) {
      log('useOrcamento', 'fetchServicos', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch origens (sources)
   */
  const fetchOrigens = async () => {
    log('useOrcamento', 'fetchOrigens', 'Fetching origens');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/origens/buscaOrigens', { responseContract: 'Origens_BuscaOrigensResponse' });
      log('useOrcamento', 'fetchOrigens', 'Response', response);

      if (!response.dados) {
        throw new Error('Erro ao buscar origens');
      }

      return Object.entries(response.dados).map(([id, name]) => ({ ID: id, Name: name }));
    } catch (err) {
      log('useOrcamento', 'fetchOrigens', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Download orcamento PDF
   */
  const downloadOrcamentoPDF = async (orcamentoId: any, htmlContent: any) => {
    log('useOrcamento', 'downloadOrcamentoPDF', 'Generating PDF', orcamentoId);
    isLoading.value = true;
    error.value = null;

    try {
      const payload: Orcamentos_CriarOrcamentoPDFInput = {
        HTML: htmlContent,
        owner_id: orcamentoId
      };
      validate('Orcamentos_CriarOrcamentoPDFInput', payload);
      const response = await httpService.post('/index.php/orcamentos/criarOrcamentoPDF', payload, {
        responseContract: 'Orcamentos_CriarOrcamentoPDFResponse',
      });
      log('useOrcamento', 'downloadOrcamentoPDF', 'Response', response);

      if (!response.success || !response.dados) {
        throw new Error(response.error || 'Erro ao gerar PDF');
      }

      return { dados: response.dados };
    } catch (err) {
      log('useOrcamento', 'downloadOrcamentoPDF', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Consulta CEP (Brazil postal code lookup)
   */
  const consultaCep = async (cep: any) => {
    log('useOrcamento', 'consultaCep', 'Looking up CEP', cep);
    isLoading.value = true;
    error.value = null;

    try {
      // Remove non-digits
      const cleanCep = cep.replace(/\D/g, '');

      if (cleanCep.length !== 8) {
        throw new Error('CEP inválido');
      }

      const response = await httpService.get(`/index.php/consulta_cep/consulta/${cleanCep}`, { responseContract: 'ConsultaCep_ConsultaResponse' });
      log('useOrcamento', 'consultaCep', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'CEP não encontrado');
      }
    } catch (err) {
      log('useOrcamento', 'consultaCep', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch orcamentos by client ID
   */
  const fetchOrcamentosByCliente = async (clienteId: number) => {
    log('useOrcamento', 'fetchOrcamentosByCliente', 'Fetching orcamentos by client', clienteId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/orcamentos/busca_orcamentos_do_cliente/${clienteId}`, {
        responseContract: 'Orcamentos_BuscaOrcamentosDoClienteResponse',
      });
      log('useOrcamento', 'fetchOrcamentosByCliente', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar orçamentos do cliente');
      }
    } catch (err) {
      log('useOrcamento', 'fetchOrcamentosByCliente', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchOrcamentos,
    fetchOrcamento,
    buscarOrcamento: fetchOrcamento, // Alias for compatibility
    createOrcamento,
    updateOrcamento,
    cancelOrcamento,
    deleteOrcamento,
    aprovarOrcamento,
    buscarOrcamentosForaDaAgenda,
    changeStatus,
    fetchServicos,
    fetchOrigens,
    downloadOrcamentoPDF,
    consultaCep,
    fetchOrcamentosByCliente
  };
}
